import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Bot, Send, Sparkles, Loader2, CheckCircle, MessageCircle } from 'lucide-react';

// Phase 111 — Public AI Sports Intake Bot widget. Renders on the public
// Wall-of-Champions page. Talks to POST /api/public/academy/{slug}/sport-intake.
// The backend uses Emergent LLM (gpt-4o-mini) with a tightly-scoped JSON-envelope
// prompt that captures student_name, student_age, skill_level, preferred_center,
// parent_name, parent_phone in 3-5 short turns. On `complete=true` the captured
// payload lands in `inbound_leads` as `interested` stage.

const SESSION_KEY = (slug) => `outplay_intake_${slug}`;

function makeSessionId() {
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export default function PublicSportIntakeBot({ slug, academyName, sport }) {
  // sessionStorage so a page refresh resumes the same conversation
  const [sessionId] = useState(() => {
    try {
      const stored = sessionStorage.getItem(SESSION_KEY(slug));
      if (stored) return stored;
      const fresh = makeSessionId();
      sessionStorage.setItem(SESSION_KEY(slug), fresh);
      return fresh;
    } catch {
      return makeSessionId();
    }
  });

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => ([
    {
      role: 'assistant',
      content: `Hi! I'm the AI assistant for ${academyName || 'this academy'}. I can help your child get started with ${sport || 'training'} in just a few quick questions. Want to begin?`,
    },
  ]));
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [captured, setCaptured] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sendTurn = async (text) => {
    const userMsg = { role: 'user', content: text };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    setSending(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/public/academy/${slug}/sport-intake`,
        { session_id: sessionId, messages: nextMessages },
        { timeout: 45000 },
      );
      setMessages((prev) => [...prev, { role: 'assistant', content: data.reply || '…' }]);
      if (data.complete && data.captured) {
        setCaptured(true);
      }
    } catch (e) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: "I'm having trouble right now — please reach out to the academy on WhatsApp instead.",
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = (input || '').trim();
    if (!trimmed || sending || captured) return;
    sendTurn(trimmed);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-50 h-14 px-5 rounded-full shadow-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white text-sm font-semibold flex items-center gap-2 hover:scale-105 transition-transform"
        data-testid="public-bot-launcher"
      >
        <Bot className="h-5 w-5" />
        <span className="hidden sm:inline">Chat with AI Coach</span>
        <span className="sm:hidden">AI Coach</span>
        <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-white animate-pulse" />
      </button>
    );
  }

  return (
    <Card className="fixed bottom-5 right-5 w-[92vw] max-w-[400px] z-50 shadow-2xl border-2 border-violet-200 flex flex-col"
          style={{ height: 'min(580px, 80vh)' }}
          data-testid="public-bot-panel">
      <CardHeader className="py-2.5 px-3 bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white rounded-t-md">
        <CardTitle className="text-sm font-semibold flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Bot className="h-4 w-4" />
            AI Coach — {academyName || 'Academy'}
          </span>
          <button type="button" onClick={() => setOpen(false)} aria-label="Minimise"
                  className="h-6 w-6 rounded hover:bg-white/20 flex items-center justify-center text-base leading-none"
                  data-testid="public-bot-close">
            ×
          </button>
        </CardTitle>
        <p className="text-[10px] text-violet-100 mt-0.5">
          Powered by Out-Play AI · {sport ? `${sport} intake` : 'Intake'}
        </p>
      </CardHeader>

      <CardContent className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50" ref={scrollRef}
                   data-testid="public-bot-thread">
        {messages.map((m, idx) => (
          <div key={idx}
               className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
               data-testid={`public-bot-msg-${m.role}-${idx}`}>
            <div className={`max-w-[80%] px-3 py-2 rounded-lg text-xs leading-relaxed ${
              m.role === 'user'
                ? 'bg-violet-600 text-white rounded-br-sm'
                : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
            }`}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start" data-testid="public-bot-typing">
            <div className="bg-white border border-slate-200 px-3 py-2 rounded-lg rounded-bl-sm">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-violet-500" />
            </div>
          </div>
        )}
        {captured && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-[11px] text-emerald-800"
               data-testid="public-bot-captured-banner">
            <p className="font-semibold flex items-center gap-1.5">
              <CheckCircle className="h-3.5 w-3.5" /> All set!
            </p>
            <p className="mt-0.5">A coach from {academyName} will reach out to you on WhatsApp shortly. Welcome to the team 🏆</p>
          </div>
        )}
      </CardContent>

      <form onSubmit={handleSubmit} className="border-t border-slate-200 p-2 bg-white rounded-b-md">
        <div className="flex gap-1.5">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={captured ? "Conversation closed" : "Type your reply…"}
            disabled={sending || captured}
            className="h-9 text-xs"
            data-testid="public-bot-input"
          />
          <Button type="submit" size="sm" disabled={sending || captured || !input.trim()}
                  className="h-9 px-3 bg-violet-600 hover:bg-violet-700 shrink-0"
                  data-testid="public-bot-send">
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </Button>
        </div>
        <p className="text-[9px] text-slate-400 mt-1 text-center">
          <Sparkles className="h-2.5 w-2.5 inline" /> AI-powered intake · Your details stay with {academyName || 'the academy'}
        </p>
      </form>
    </Card>
  );
}
